import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const EvidenceAttachment = ({ caseId, onAttachmentAdd }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [sourceUrl, setSourceUrl] = useState('');

  // Mock evidence attachments
  const mockAttachments = [
    {
      id: 'att_1',
      type: 'image',
      name: 'climate_data_chart.png',
      url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400',
      size: '2.4 MB',
      uploadedBy: 'Dr. Elena Rossi',
      uploadedAt: new Date(Date.now() - 3600000),
      verified: true,
      metadata: {
        dimensions: '1920x1080',
        format: 'PNG',
        hash: 'sha256:a1b2c3d4...'
      }
    },
    {
      id: 'att_2',
      type: 'document',
      name: 'ipcc_report_excerpt.pdf',
      url: '/documents/ipcc_report.pdf',
      size: '1.8 MB',
      uploadedBy: 'Marcus Weber',
      uploadedAt: new Date(Date.now() - 1800000),
      verified: true,
      metadata: {
        pages: 15,
        format: 'PDF',
        hash: 'sha256:e5f6g7h8...'
      }
    },
    {
      id: 'att_3',
      type: 'video',
      name: 'expert_interview.mp4',
      url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
      size: '45.2 MB',
      uploadedBy: 'You',
      uploadedAt: new Date(Date.now() - 900000),
      verified: false,
      metadata: {
        duration: '00:03:45',
        format: 'MP4',
        resolution: '1280x720'
      }
    },
    {
      id: 'att_4',
      type: 'link',
      name: 'Original Source Article',
      url: 'https://example.com/climate-study-2024',
      uploadedBy: 'Dr. Elena Rossi',
      uploadedAt: new Date(Date.now() - 7200000),
      verified: true,
      metadata: {
        domain: 'nature.com',
        title: 'Climate Change Impact Study 2024',
        archived: true
      }
    }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleFileUpload = (files) => {
    files?.forEach((file) => {
      setUploadProgress({ name: file?.name, progress: 0 });
      
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev?.progress >= 100) {
            clearInterval(interval);
            setTimeout(() => setUploadProgress(null), 1000);
            return prev;
          }
          return { ...prev, progress: prev?.progress + 10 };
        });
      }, 200);
    });
  };

  const handleSourceAdd = () => {
    if (sourceUrl?.trim()) {
      onAttachmentAdd?.({
        type: 'link',
        url: sourceUrl,
        name: 'External Source'
      });
      setSourceUrl('');
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return 'Image';
      case 'document': return 'FileText';
      case 'video': return 'Video';
      case 'link': return 'Link';
      default: return 'File';
    }
  };

  const getFileTypeColor = (type) => {
    switch (type) {
      case 'image': return 'text-blue-600';
      case 'document': return 'text-red-600';
      case 'video': return 'text-purple-600';
      case 'link': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Paperclip" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Evidence & Sources</h3>
            <p className="text-sm text-muted-foreground">
              {mockAttachments?.length} attachments • {mockAttachments?.filter(a => a?.verified)?.length} verified
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Search"
          >
            Search
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Filter"
          >
            Filter
          </Button>
        </div>
      </div>
      {/* Upload Area */}
      <div className="p-4 border-b border-border">
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-smooth
            ${dragActive 
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Icon name="Upload" size={32} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground mb-1">
            Drop files here or click to upload
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Supports images, documents, videos, and audio files
          </p>
          
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Upload"
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              Choose Files
            </Button>
            <input
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileUpload(Array.from(e?.target?.files))}
            />
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{uploadProgress?.name}</span>
                <span className="text-sm text-muted-foreground">{uploadProgress?.progress}%</span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress?.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Add Source URL */}
        <div className="mt-4 flex space-x-2">
          <div className="flex-1">
            <Input
              placeholder="Add source URL (website, social media, etc.)"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e?.target?.value)}
            />
          </div>
          <Button
            variant="outline"
            iconName="Plus"
            onClick={handleSourceAdd}
            disabled={!sourceUrl?.trim()}
          >
            Add Source
          </Button>
        </div>
      </div>
      {/* Attachments List */}
      <div className="max-h-96 overflow-y-auto">
        {mockAttachments?.map((attachment) => (
          <div
            key={attachment?.id}
            className="flex items-center p-4 border-b border-border last:border-b-0 hover:bg-muted/30 transition-smooth"
          >
            {/* File Preview/Icon */}
            <div className="flex-shrink-0 mr-4">
              {attachment?.type === 'image' ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden border border-border">
                  <Image
                    src={attachment?.url}
                    alt={attachment?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                  <Icon
                    name={getFileIcon(attachment?.type)}
                    size={20}
                    className={getFileTypeColor(attachment?.type)}
                  />
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-medium text-foreground truncate">
                  {attachment?.name}
                </h4>
                {attachment?.verified && (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 rounded-full">
                    <Icon name="Shield" size={12} className="text-success" />
                    <span className="text-xs font-medium text-success">Verified</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>{attachment?.size || 'External link'}</span>
                <span>•</span>
                <span>by {attachment?.uploadedBy}</span>
                <span>•</span>
                <span>{attachment?.uploadedAt?.toLocaleTimeString()}</span>
              </div>

              {/* Metadata */}
              {attachment?.metadata && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(attachment?.metadata)?.map(([key, value]) => (
                    <span
                      key={key}
                      className="px-2 py-1 bg-muted rounded text-xs text-muted-foreground"
                    >
                      {key}: {value}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 ml-4">
              <Button
                variant="ghost"
                size="icon"
                iconName="Eye"
                onClick={() => {
                  // Preview attachment
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                iconName="Download"
                onClick={() => {
                  // Download attachment
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                iconName="MoreHorizontal"
                onClick={() => {
                  // More options
                }}
              />
            </div>
          </div>
        ))}
      </div>
      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/20">
        <div className="text-sm text-muted-foreground">
          Total size: 49.4 MB • {mockAttachments?.filter(a => a?.verified)?.length}/{mockAttachments?.length} verified
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Archive"
          >
            Archive All
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Share"
          >
            Share Evidence
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EvidenceAttachment;